// src/pages/modules/Archive/Archive.tsx
// คลังข้อมูล — ลูกค้าที่จบแล้ว (data_d) + หนี้เสีย (data_e)
// + แก้ไขข้อมูล (register/loan) แบบ version + ย้อนกลับเวอร์ชันได้ (super admin)

import React from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { AuthContext, dbRead, dbSet, saveLog } from '../../../_services/firebase'
import { PATH } from '../../../_services/customer.service'
import {
  DB_CUST_C, DB_CUST_D, DB_CUST_E, DB_LOAN_BY_ID, DB_ARCHIVE_VERSION_BY_ID,
  DB_PHOTO_ITEM,
} from '../../../_config/db_paths'
import { formatCurrency, formatDate, toastDanger, toastSuccess } from '../../../_utils'
import { loadPhotos as fetchPhotos, deletePhoto } from '../../../_services/photo.service'
import type { CustomerReg, LoanData, InstallmentItem } from '../../../_types'
import ConfirmAlert from '../../../components/ConfirmAlert/ConfirmAlert'

type ArchiveTab = 'online' | 'closed' | 'badDebt'

function calcRemain(loan: LoanData, ins: InstallmentItem[]): number {
  const paid = ins.reduce((s, i) => s + (i.paidAmount || 0), 0)
  return Math.max(0, loan.totalWithInterest - paid)
}

// path ของลูกค้าตาม tab (เขียนกลับที่เดิม)
function custPath(tab: ArchiveTab, key: string): string {
  return tab === 'closed' ? DB_CUST_D(key) : tab === 'badDebt' ? DB_CUST_E(key) : DB_CUST_C(key)
}

function formatSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
interface PhotoRecord {
  id:         string
  name:       string
  image?:     string
  base64?:    string
  url?:       string
  size?:      number
  type?:      string
  sort?:      number
  createdAt?: number
}

// ─── CustomerRow ──────────────────────────────────────────────
function ArchiveCard({
  reg, loan, installments, onDetail, onEdit,
}: {
  reg: CustomerReg
  loan: LoanData | null
  installments: InstallmentItem[]
  onDetail: () => void
  onEdit: () => void
}) {
  const remain = loan ? calcRemain(loan, installments) : 0
  const paid = installments.filter((i) => i.status === 'paid').length
  const isBad = reg.meta.status === 'stopped'

  return (
    <Card className='shadow-sm' style={{ fontSize: 13 }}>
      <Card.Body>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div>
            <div style={{ fontWeight: 700 }}>{reg.personal.fullName}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{formatDate(reg.meta.updatedAt)}</div>
          </div>
          <Badge bg={isBad ? 'danger' : 'secondary'} style={{ fontSize: 10 }}>
            {isBad ? 'หนี้เสีย' : 'ปิดบัญชี'}
          </Badge>
        </div>
        {loan && (
          <div>
            <div>ยอดกู้: <strong>{formatCurrency(loan.actualAmount)}</strong></div>
            <div>ยอดรวม: <strong>{formatCurrency(loan.totalWithInterest)}</strong></div>
            <div>จ่ายแล้ว: <strong>{paid}/{installments.length} งวด</strong></div>
            {isBad && <div style={{ color: '#c62828', fontWeight: 700 }}>คงเหลือ (ค้าง): {formatCurrency(remain)}</div>}
            <div>โซน: <strong>{loan.zone || '-'}</strong></div>
          </div>
        )}
        <Button variant='outline-warning' size='sm' className='w-100 mt-3' onClick={onEdit}>
          <i className='fa-solid fa-pen-to-square me-1' />แก้ไขข้อมูล
        </Button>
        <Button variant='outline-secondary' size='sm' className='w-100 mt-2' onClick={onDetail}>
          ดูรายละเอียด
        </Button>
      </Card.Body>
    </Card>
  )
}

// ─── Detail Modal ─────────────────────────────────────────────
function DetailModal({ show, reg, loan, installments, onClose }: {
  show: boolean
  reg: CustomerReg | null
  loan: LoanData | null
  installments: InstallmentItem[]
  onClose: () => void
}) {
  if (!reg) return null
  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          {reg.personal.fullName}
          <Badge bg={reg.meta.status === 'stopped' ? 'danger' : 'secondary'} className='ms-2' style={{ fontSize: 10 }}>
            {reg.meta.status === 'stopped' ? 'หนี้เสีย' : 'ปิดบัญชี'}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='g-3'>
          <Col xs={12} md={6}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลส่วนตัว</Card.Header>
              <Card.Body>
                <Table size='sm' style={{ fontSize: 12, marginBottom: 0 }}>
                  <tbody>
                    {[
                      ['ชื่อ', reg.personal.fullName],
                      ['เลขบัตร', reg.personal.nationalId || '-'],
                      ['วันเกิด', reg.personal.birthDate || '-'],
                      ['โทรศัพท์', reg.personal.phone || '-'],
                      ['ที่อยู่', reg.personal.address || '-'],
                    ].map(([k, v]) => (
                      <tr key={k}><td style={{ fontWeight: 600, color: '#666', width: '40%' }}>{k}</td><td>{v}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            {loan && (
              <Card className='shadow-sm'>
                <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลสินเชื่อ</Card.Header>
                <Card.Body>
                  <Table size='sm' style={{ fontSize: 12, marginBottom: 0 }}>
                    <tbody>
                      {[
                        ['ยอดกู้', `${formatCurrency(loan.actualAmount)} กีบ`],
                        ['ดอกเบี้ย', `${loan.interestRate}%`],
                        ['ยอดรวม', `${formatCurrency(loan.totalWithInterest)} กีบ`],
                        ['งวด', `${loan.installmentDays} วัน`],
                        ['ส่ง/วัน', `${formatCurrency(loan.dailyPayment)} กีบ`],
                        ['โซน', loan.zone || '-'],
                        ['วัตถุประสงค์', loan.purpose || '-'],
                      ].map(([k, v]) => (
                        <tr key={k}><td style={{ fontWeight: 600, color: '#666', width: '40%' }}>{k}</td><td>{v}</td></tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>
          <Col xs={12}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ประวัติการชำระ ({installments.length} งวด)</Card.Header>
              <Card.Body style={{ padding: 0, maxHeight: 250, overflowY: 'auto' }}>
                <Table size='sm' bordered style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead className='table-light sticky-top'>
                    <tr><th>งวด</th><th>ยอด</th><th>จ่ายจริง</th><th>สถานะ</th></tr>
                  </thead>
                  <tbody>
                    {installments.map((ins) => (
                      <tr key={ins.seq}>
                        <td>{ins.seq}</td>
                        <td>{formatCurrency(ins.dueAmount)}</td>
                        <td>{ins.paidAmount > 0 ? formatCurrency(ins.paidAmount) : '-'}</td>
                        <td>
                          <Badge bg={ins.status === 'paid' ? 'success' : ins.status === 'partial' ? 'warning' : 'secondary'} style={{ fontSize: 10 }}>
                            {ins.status === 'paid' ? 'จ่ายแล้ว' : ins.status === 'partial' ? 'บางส่วน' : 'ไม่ได้จ่าย'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {installments.length === 0 && (
                      <tr><td colSpan={4} className='text-center text-muted'>ไม่มีประวัติ</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose}>ปิด</Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── Edit picture ─────────────────────────────────────────────
function PhotoManager({ customerId, uid }: { customerId: string; uid: string }) {
  const [photos, setPhotos] = React.useState<PhotoRecord[]>([])
  const [loading, setLoading] = React.useState(true)
  const [uploading, setUploading] = React.useState(false)
  const [deleting, setDeleting] = React.useState<string | null>(null)
  const [preview, setPreview] = React.useState<PhotoRecord | null>(null)
  const [confirmDel, setConfirmDel] = React.useState<PhotoRecord | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // load existing photos
  React.useEffect(() => {
    ; (async () => {
      try {
        const list = await fetchPhotos(customerId)
        setPhotos(list as PhotoRecord[])
      } catch { setPhotos([]) }
      finally { setLoading(false) }
    })()
  }, [customerId])

  // resize
  const resizeImage = (file: File, maxSize = 800, quality = 0.55): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > maxSize || height > maxSize) {
          if (width >= height) { height = Math.round(height * maxSize / width); width = maxSize }
          else { width = Math.round(width * maxSize / height); height = maxSize }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('resize failed')),
          'image/jpeg', quality
        )
      }
      img.onerror = reject
      img.src = url
    })

  // upload new photos
  const handleAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const newPhotos: PhotoRecord[] = []
      for (let i = 0; i < files.length; i++) {
        const resized = await resizeImage(files[i])
        await new Promise<void>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = async () => {
            try {
              const base64 = (reader.result as string).split(',')[1]
              const ts = Date.now()
              const rand = Math.random().toString(36).slice(2, 8)
              const key = `photo_${ts}_${rand}`
              const rec: PhotoRecord = {
                id: key,
                image: base64,
                name: files[i].name,
                size: resized.size,
                type: 'image/jpeg',
                sort: photos.length + i + 1,
                createdAt: ts,
              }
              await dbSet(DB_PHOTO_ITEM(customerId, key), { ...rec, uploadedBy: uid })
              newPhotos.push(rec)
              resolve()
            } catch (err) { reject(err) }
          }
          reader.onerror = reject
          reader.readAsDataURL(resized)
        })
      }
      setPhotos((prev) => [...prev, ...newPhotos])
      await saveLog(uid, 'update', customerId, `เพิ่มรูปภาพ ${files.length} รูป`)
      toastSuccess(`เพิ่มรูป ${files.length} รูปเรียบร้อย`)
    } catch (e: any) {
      toastDanger(e?.message || 'อัปโหลดไม่สำเร็จ')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  // delete photo
  const handleDelete = async (photo: PhotoRecord) => {
    setDeleting(photo.id)
    try {
      await deletePhoto(customerId, photo.id)
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id))
      await saveLog(uid, 'update', customerId, `ลบรูปภาพ ${photo.name}`)
      toastSuccess('ลบรูปเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'ลบไม่สำเร็จ')
    } finally {
      setDeleting(null)
      setConfirmDel(null)
    }
  }

  if (loading) return (
    <div className='d-flex justify-content-center p-3'>
      <Spinner animation='border' size='sm' variant='warning' />
      <span className='ms-2 text-muted' style={{ fontSize: 12 }}>กำลังโหลดรูปภาพ...</span>
    </div>
  )

  function toDataUrl(p: PhotoRecord): string {
    if (!p) return ''
    if (p.url) return p.url
    if (p.base64) return p.base64.startsWith('data:') ? p.base64 : `data:image/jpeg;base64,${p.base64}`
    if (p.image) return `data:image/jpeg;base64,${p.image}`
    return ''
  }

  return (
    <>
      <ConfirmAlert
        show={!!confirmDel}
        title='ยืนยันลบรูปภาพ'
        message={`ลบรูป "${confirmDel?.name}" ออก? ไม่สามารถย้อนกลับได้`}
        confirmLabel={deleting ? 'กำลังลบ...' : 'ลบ'}
        variant='danger'
        onConfirm={() => confirmDel && handleDelete(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />

      {/* Toolbar */}
      <div className='d-flex align-items-center gap-2 mb-3'>
        <input ref={inputRef} type='file' accept='image/*' multiple style={{ display: 'none' }} onChange={handleAdd} disabled={uploading} />
        <Button size='sm' variant='outline-primary' disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading
            ? <><Spinner size='sm' animation='border' className='me-1' />กำลังอัปโหลด...</>
            : <><i className='fa-solid fa-plus me-1' />เพิ่มรูปภาพ</>
          }
        </Button>
        <span className='text-muted' style={{ fontSize: 12 }}>
          {photos.length} รูป
          {photos.length > 0 && ` · รวม ${(photos.reduce((s, p) => s + (p.size || 0), 0) / 1024).toFixed(0)} KB`}
        </span>
      </div>

      {/* Grid */}
      {photos.length === 0 && (
        <div className='text-muted' style={{ fontSize: 12 }}>ยังไม่มีรูปภาพ กดเพิ่มด้านบน</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
        {photos.map((p) => {
          const src = toDataUrl(p)

          return (
            <div key={p.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              {/* thumbnail */}
              <div style={{ cursor: 'pointer' }} onClick={() => setPreview(p)}>
                {src
                  ? <img src={src} alt={p.name} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                  : <div style={{ height: 100, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className='fa-solid fa-image' style={{ color: '#ccc', fontSize: 24 }} />
                  </div>
                }
              </div>
              {/* delete button */}
              <button
                onClick={() => setConfirmDel(p)}
                disabled={deleting === p.id}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: 'rgba(198,40,40,0.85)', color: '#fff',
                  border: 'none', borderRadius: 999,
                  width: 22, height: 22, fontSize: 11,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {deleting === p.id ? <Spinner size='sm' animation='border' style={{ width: 12, height: 12 }} /> : '✕'}
              </button>
              {/* caption */}
              <div style={{ fontSize: 9, padding: '2px 5px', background: '#fafafa', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}{p.size ? ` · ${formatSize(p.size)}` : ''}
              </div>
            </div>
          )
        })}
      </div>

      {/* Full preview modal */}
      <Modal show={!!preview} onHide={() => setPreview(null)} centered size='xl'>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>
            {preview?.name}
            {preview?.size && <span className='text-muted ms-2' style={{ fontSize: 11 }}>({formatSize(preview.size)})</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 8, textAlign: 'center', background: '#111' }}>
          {preview && <img src={toDataUrl(preview)} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'space-between' }}>
          <div className='d-flex gap-2'>
            <Button size='sm' variant='outline-secondary'
              disabled={(photos.findIndex((p) => p.id === preview?.id)) <= 0}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i > 0) setPreview(photos[i - 1]) }}
            ><i className='fa-solid fa-arrow-left me-1' />ก่อนหน้า</Button>
            <Button size='sm' variant='outline-secondary'
              disabled={(photos.findIndex((p) => p.id === preview?.id)) >= photos.length - 1}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i < photos.length - 1) setPreview(photos[i + 1]) }}
            >ถัดไป<i className='fa-solid fa-arrow-right ms-1' /></Button>
          </div>
          <span style={{ fontSize: 12, color: '#888' }}>
            {photos.findIndex((p) => p.id === preview?.id) + 1} / {photos.length}
          </span>
          <Button size='sm' variant='outline-danger' onClick={() => { setConfirmDel(preview); setPreview(null) }}>
            <i className='fa-solid fa-trash me-1' />ลบรูปนี้
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

// ─── EditModal (แก้ไข register/loan + version + ย้อนกลับ) ──────
interface ArchiveVersion {
  versionId: string
  savedAt: number
  savedBy: string
  reg: CustomerReg
  loan: LoanData | null
}

const EMPTY_P = {
  fullName: '', nationalId: '', birthDate: '', age: '', phone: '',
  address: '', maritalStatus: '', facebookLink: '', locationLink: '',
}
const EMPTY_L = {
  actualAmount: '', wantedAmount: '', interestRate: '', installmentDays: '',
  dailyPayment: '', totalWithInterest: '', zone: '', purpose: '',
}

function EditModal({ show, reg, loan, tab, uid, onClose, onReload }: {
  show: boolean
  reg: CustomerReg | null
  loan: LoanData | null
  tab: ArchiveTab
  uid: string
  onClose: () => void
  onReload: () => void
}) {
  const [p, setP] = React.useState({ ...EMPTY_P })
  const [ln, setLn] = React.useState({ ...EMPTY_L })
  const [versions, setVersions] = React.useState<ArchiveVersion[]>([])
  const [loadingV, setLoadingV] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const initForm = (r: CustomerReg | null, l: LoanData | null) => {
    const pp: any = r?.personal || {}
    setP({
      fullName: pp.fullName || '', nationalId: pp.nationalId || '', birthDate: pp.birthDate || '',
      age: pp.age != null ? String(pp.age) : '', phone: pp.phone || '', address: pp.address || '',
      maritalStatus: pp.maritalStatus || '', facebookLink: pp.facebookLink || '', locationLink: pp.locationLink || '',
    })
    setLn(l ? {
      actualAmount: String(l.actualAmount ?? ''), wantedAmount: String(l.wantedAmount ?? ''),
      interestRate: String(l.interestRate ?? ''), installmentDays: String(l.installmentDays ?? ''),
      dailyPayment: String(l.dailyPayment ?? ''), totalWithInterest: String(l.totalWithInterest ?? ''),
      zone: Array.isArray((l as any).zone) ? (l as any).zone.join('+') : (l.zone || ''), purpose: l.purpose || '',
    } : { ...EMPTY_L })
  }

  const loadVersions = async (key: string) => {
    setLoadingV(true)
    try {
      const raw = await dbRead<Record<string, ArchiveVersion>>(DB_ARCHIVE_VERSION_BY_ID(key))
      const list = Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0))
      setVersions(list)
    } catch {
      setVersions([])
    } finally {
      setLoadingV(false)
    }
  }

  React.useEffect(() => {
    if (!show || !reg) return
    initForm(reg, loan)
    loadVersions(reg.key)
  }, [show, reg?.key])

  if (!reg) return null

  // สร้าง reg/loan ใหม่จากฟอร์ม
  const buildNew = () => {
    const now = Date.now()
    const newReg: CustomerReg = {
      ...reg,
      personal: {
        ...reg.personal,
        fullName: p.fullName, nationalId: p.nationalId, birthDate: p.birthDate, age: p.age,
        phone: p.phone, address: p.address, maritalStatus: p.maritalStatus,
        facebookLink: p.facebookLink, locationLink: p.locationLink,
      } as any,
      meta: { ...reg.meta, updatedAt: now },
    }
    const newLoan: LoanData | null = loan ? {
      ...loan,
      actualAmount: Number(ln.actualAmount) || 0,
      wantedAmount: Number(ln.wantedAmount) || 0,
      interestRate: Number(ln.interestRate) || 0,
      installmentDays: parseInt(ln.installmentDays) || 0,
      dailyPayment: Number(ln.dailyPayment) || 0,
      totalWithInterest: Number(ln.totalWithInterest) || 0,
      zone: ln.zone,
      purpose: ln.purpose,
      updatedAt: now,
      updatedBy: uid,
    } : null
    return { newReg, newLoan }
  }

  const sig = (personal: any, l: LoanData | null) => JSON.stringify({ personal, loan: l })

  const nextVersionId = () => {
    const today = new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10)   // เวลาไทย
    const n = versions.filter((v) => (v.versionId || '').startsWith(today)).length + 1
    return `${today}_${n}`
  }

  const save = async () => {
    if (!p.fullName.trim()) { toastDanger('กรุณากรอกชื่อ'); return }
    setSaving(true)
    try {
      const { newReg, newLoan } = buildNew()

      // สร้าง version ใหม่เฉพาะเมื่อข้อมูลต่างจากเวอร์ชันล่าสุด
      const latest = versions[0]
      const changed = !latest || sig(latest.reg?.personal, latest.loan) !== sig(newReg.personal, newLoan)
      let versionId = latest?.versionId || ''
      if (changed) {
        versionId = nextVersionId()
        const vdoc: ArchiveVersion = { versionId, savedAt: Date.now(), savedBy: uid, reg: newReg, loan: newLoan }
        await dbSet(`${DB_ARCHIVE_VERSION_BY_ID(reg.key)}/${versionId}`, vdoc)
      }

      // เขียนข้อมูลจริง
      await dbSet(custPath(tab, reg.key), { ...newReg, meta: { ...newReg.meta, archiveVersion: versionId } })
      if (newLoan) await dbSet(DB_LOAN_BY_ID(reg.key), newLoan)

      await saveLog(uid, 'update', reg.key, `แก้ไขข้อมูลคลัง ${newReg.personal.fullName}${changed ? ` (version ${versionId})` : ' (ไม่เปลี่ยน)'}`)
      toastSuccess(changed ? `บันทึก + สร้าง version ${versionId}` : 'บันทึกแล้ว (ข้อมูลไม่เปลี่ยน)')
      await loadVersions(reg.key)
      onReload()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  // ย้อนกลับไปใช้เวอร์ชันเก่า (ไม่สร้าง version ใหม่)
  const restore = async (v: ArchiveVersion) => {
    if (!window.confirm(`ย้อนกลับไปใช้ข้อมูล version ${v.versionId}?\nข้อมูลปัจจุบันจะถูกแทนที่`)) return
    setSaving(true)
    try {
      const now = Date.now()
      await dbSet(custPath(tab, reg.key), { ...v.reg, meta: { ...v.reg.meta, updatedAt: now, archiveVersion: v.versionId } })
      if (v.loan) await dbSet(DB_LOAN_BY_ID(reg.key), v.loan)
      await saveLog(uid, 'update', reg.key, `ย้อนข้อมูลคลัง ${v.reg?.personal?.fullName || ''} → version ${v.versionId}`)
      initForm(v.reg, v.loan)   // อัปเดตฟอร์มให้ตรงเวอร์ชันที่เลือก
      toastSuccess(`ใช้ version ${v.versionId} แล้ว`)
      onReload()
    } catch (e: any) {
      toastDanger(e?.message || 'ย้อนกลับไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  // helper render field (เป็นฟังก์ชันคืน JSX — ไม่ใช่ component ป้องกัน focus หลุด)
  const field = (label: string, value: string, on: (v: string) => void, type = 'text') => (
    <Form.Group className='mb-2'>
      <Form.Label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{label}</Form.Label>
      <Form.Control size='sm' type={type} value={value} onChange={(e) => on(e.target.value)} />
    </Form.Group>
  )

  return (
    <Modal show={show} onHide={() => !saving && onClose()} size='xl' centered scrollable>
      <Modal.Header closeButton={!saving}>
        <Modal.Title style={{ fontSize: 15 }}>
          <i className='fa-solid fa-pen-to-square me-2 text-warning' />แก้ไขข้อมูล: {reg.personal.fullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='info' style={{ fontSize: 12 }}>
          ทุกครั้งที่บันทึกและข้อมูลเปลี่ยน จะสร้าง <strong>version</strong> อัตโนมัติ และย้อนกลับไปใช้เวอร์ชันเก่าได้จากด้านล่าง
        </Alert>
        <Row className='g-3'>
          {/* ── ข้อมูลส่วนตัว (register) ── */}
          <Col xs={12} md={6}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลส่วนตัว (ลงทะเบียน)</Card.Header>
              <Card.Body>
                {field('ชื่อ-นามสกุล', p.fullName, (v) => setP({ ...p, fullName: v }))}
                {field('เลขบัตร', p.nationalId, (v) => setP({ ...p, nationalId: v }))}
                <Row className='g-2'>
                  <Col xs={6}>{field('วันเกิด', p.birthDate, (v) => setP({ ...p, birthDate: v }))}</Col>
                  <Col xs={6}>{field('อายุ', p.age, (v) => setP({ ...p, age: v }))}</Col>
                </Row>
                {field('โทรศัพท์', p.phone, (v) => setP({ ...p, phone: v }))}
                {field('ที่อยู่', p.address, (v) => setP({ ...p, address: v }))}
                {field('สถานะภาพ', p.maritalStatus, (v) => setP({ ...p, maritalStatus: v }))}
                {field('Facebook', p.facebookLink, (v) => setP({ ...p, facebookLink: v }))}
                {field('โลเคชั่น (ลิงก์)', p.locationLink, (v) => setP({ ...p, locationLink: v }))}
              </Card.Body>
            </Card>
          </Col>

          {/* ── ข้อมูลสินเชื่อ (loan) ── */}
          <Col xs={12} md={6}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลสินเชื่อ</Card.Header>
              <Card.Body>
                {loan ? (
                  <>
                    <Row className='g-2'>
                      <Col xs={6}>{field('ยอดกู้จริง', ln.actualAmount, (v) => setLn({ ...ln, actualAmount: v }), 'number')}</Col>
                      <Col xs={6}>{field('วงเงินที่ขอ', ln.wantedAmount, (v) => setLn({ ...ln, wantedAmount: v }), 'number')}</Col>
                    </Row>
                    <Row className='g-2'>
                      <Col xs={6}>{field('ดอกเบี้ย (%)', ln.interestRate, (v) => setLn({ ...ln, interestRate: v }), 'number')}</Col>
                      <Col xs={6}>{field('จำนวนงวด (วัน)', ln.installmentDays, (v) => setLn({ ...ln, installmentDays: v }), 'number')}</Col>
                    </Row>
                    <Row className='g-2'>
                      <Col xs={6}>{field('ยอดรวมทั้งสัญญา', ln.totalWithInterest, (v) => setLn({ ...ln, totalWithInterest: v }), 'number')}</Col>
                      <Col xs={6}>{field('ส่ง/วัน', ln.dailyPayment, (v) => setLn({ ...ln, dailyPayment: v }), 'number')}</Col>
                    </Row>
                    {field('โซน', ln.zone, (v) => setLn({ ...ln, zone: v }))}
                    {field('วัตถุประสงค์', ln.purpose, (v) => setLn({ ...ln, purpose: v }))}
                  </>
                ) : (
                  <div className='text-muted' style={{ fontSize: 12 }}>ไม่พบข้อมูลสินเชื่อ</div>
                )}
              </Card.Body>
            </Card>
            <Card className='shadow-sm mt-2'>
              <Card.Body style={{ fontSize: 12 }}>
                <PhotoManager customerId={reg.key} uid={uid} />
              </Card.Body>
            </Card>
          </Col>

          {/* ── เวอร์ชันข้อมูล ── */}
          <Col xs={12}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>
                <i className='fa-solid fa-clock-rotate-left me-2' />เวอร์ชันข้อมูล ({versions.length})
              </Card.Header>
              <Card.Body style={{ fontSize: 12, maxHeight: 240, overflowY: 'auto' }}>
                {loadingV && <div className='text-center'><Spinner size='sm' animation='border' /></div>}
                {!loadingV && versions.length === 0 && (
                  <div className='text-muted'>ยังไม่มีเวอร์ชัน — เมื่อบันทึกครั้งแรกที่ข้อมูลเปลี่ยน จะสร้าง version ให้อัตโนมัติ</div>
                )}
                {versions.map((v, i) => (
                  <div key={v.versionId} className='d-flex align-items-center justify-content-between p-2 mb-1'
                    style={{ background: '#f7f7f7', borderRadius: 6 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {v.versionId}
                        {i === 0 && <Badge bg='success' className='ms-2' style={{ fontSize: 9 }}>ล่าสุด</Badge>}
                        {reg.meta && (reg.meta as any).archiveVersion === v.versionId && (
                          <Badge bg='primary' className='ms-2' style={{ fontSize: 9 }}>กำลังใช้</Badge>
                        )}
                      </div>
                      <div className='text-muted' style={{ fontSize: 10 }}>
                        {formatDate(v.savedAt)} · {v.reg?.personal?.fullName || ''}
                        {v.loan ? ` · กู้ ${formatCurrency(v.loan.actualAmount)}` : ''}
                      </div>
                    </div>
                    <Button size='sm' variant='outline-primary' style={{ fontSize: 11 }}
                      disabled={saving} onClick={() => restore(v)}>
                      <i className='fa-solid fa-rotate-left me-1' />ใช้เวอร์ชันนี้
                    </Button>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose} disabled={saving}>ปิด</Button>
        <Button variant='warning' size='sm' onClick={save} disabled={saving}>
          {saving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึก</>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── Archive Main ─────────────────────────────────────────────
export default function Archive() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [activeTab, setActiveTab] = React.useState<ArchiveTab>('online')
  const [loading, setLoading] = React.useState(true)
  const [customers, setCustomers] = React.useState<CustomerReg[]>([])
  const [loans, setLoans] = React.useState<Record<string, LoanData>>({})
  const [installMap, setInstallMap] = React.useState<Record<string, InstallmentItem[]>>({})
  const [viewMode, setViewMode] = React.useState<'Grid' | 'List'>('Grid')
  const [search, setSearch] = React.useState('')
  const [zoneFilter, setZoneFilter] = React.useState('ทุกโซน')
  const [detailReg, setDetailReg] = React.useState<CustomerReg | null>(null)
  const [showDetail, setShowDetail] = React.useState(false)
  const [showEdit, setShowEdit] = React.useState(false)

  const loadData = React.useCallback(async (tab: ArchiveTab) => {
    setLoading(true)
    setCustomers([])
    try {
      const raw = await dbRead<Record<string, CustomerReg>>(
        tab === 'online' ? PATH.activeAll : tab === 'closed' ? PATH.closedAll : PATH.badDebtAll
      )
      const list = Object.values(raw || {}).sort((a, b) => b.meta.updatedAt - a.meta.updatedAt)
      setCustomers(list)

      const lMap: Record<string, LoanData> = {}
      const iMap: Record<string, InstallmentItem[]> = {}
      await Promise.all(
        list.map(async (c) => {
          const [l, ins] = await Promise.all([
            dbRead<LoanData>(PATH.loan(c.key)),
            dbRead<Record<string, InstallmentItem>>(PATH.payin(c.key)),
          ])
          if (l) lMap[c.key] = l
          if (ins) iMap[c.key] = Object.values(ins).filter(Boolean).sort((a, b) => a.seq - b.seq)
          else iMap[c.key] = []
        })
      )
      setLoans(lMap)
      setInstallMap(iMap)
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadData(activeTab) }, [activeTab, loadData])

  const allZones = React.useMemo(() => {
    const set = new Set<string>()
    customers.forEach((c) => { const z = loans[c.key]?.zone; if (z) set.add(z) })
    return Array.from(set).sort()
  }, [customers, loans])

  const filtered = customers.filter((c) => {
    const l = loans[c.key]
    const matchSearch = !search ||
      c.personal.fullName.toLowerCase().includes(search.toLowerCase()) ||
      c.personal.phone?.includes(search) ||
      c.key.includes(search) ||
      (l?.zone || '').includes(search)
    const matchZone = zoneFilter === 'ทุกโซน' || (l?.zone || '') === zoneFilter
    return matchSearch && matchZone
  })

  return (
    <div className='p-3'>
      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <h5 className='mb-0'>คลังข้อมูล</h5>
        <div className='d-flex gap-2'>
          <Button size='sm' variant={viewMode === 'Grid' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('Grid')}>Grid</Button>
          <Button size='sm' variant={viewMode === 'List' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('List')}>List</Button>
          <Button size='sm' variant='outline-secondary' onClick={() => loadData(activeTab)}>
            <i className='fa-solid fa-rotate-right' />
          </Button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className='d-flex gap-2 mb-3'>
        <Button
          size='sm'
          variant={activeTab === 'online' ? 'primary' : 'outline-primary'}
          onClick={() => setActiveTab('online')}
        >
          <i className='fa-solid fa-box-archive me-1' />บัญชีที่ทำงานอยู่
        </Button>
        <Button
          size='sm'
          variant={activeTab === 'closed' ? 'secondary' : 'outline-secondary'}
          onClick={() => setActiveTab('closed')}
        >
          <i className='fa-solid fa-box-archive me-1' />ปิดบัญชีแล้ว
        </Button>
        <Button
          size='sm'
          variant={activeTab === 'badDebt' ? 'danger' : 'outline-danger'}
          onClick={() => setActiveTab('badDebt')}
        >
          <i className='fa-solid fa-skull-crossbones me-1' />หนี้เสีย
        </Button>
      </div>

      {/* Filter */}
      <Row className='g-2 mb-3'>
        <Col xs={12} md={6}>
          <Form.Control size='sm' placeholder='ค้นหา ชื่อ / เบอร์ / key / zone' value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col xs={8} md={4}>
          <Form.Select size='sm' value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value='ทุกโซน'>ทุกโซน</option>
            {allZones.map((z) => <option key={z} value={z}>{z}</option>)}
          </Form.Select>
        </Col>
        <Col xs={4} md={2}>
          <Button size='sm' variant='outline-danger' className='w-100' onClick={() => { setSearch(''); setZoneFilter('ทุกโซน') }}>ล้าง</Button>
        </Col>
      </Row>

      {loading && (
        <div className='text-center p-4'><Spinner animation='border' variant='warning' /></div>
      )}

      {!loading && filtered.length === 0 && (
        <Alert variant='info' style={{ fontSize: 13 }}>
          ไม่พบข้อมูล{activeTab === 'online' ? 'บัญชีที่ทำงานอยู่' : activeTab === 'closed' ? 'ปิดบัญชี' : 'หนี้เสีย'}
        </Alert>
      )}

      {!loading && viewMode === 'Grid' && (
        <Row className='g-3'>
          {filtered.map((c) => (
            <Col key={c.key} xs={12} md={6} lg={4}>
              <ArchiveCard
                reg={c}
                loan={loans[c.key] || null}
                installments={installMap[c.key] || []}
                onDetail={() => { setDetailReg(c); setShowDetail(true) }}
                onEdit={() => { setDetailReg(c); setShowEdit(true) }}
              />
            </Col>
          ))}
        </Row>
      )}

      {!loading && viewMode === 'List' && (
        <Table size='sm' bordered hover style={{ fontSize: 12 }}>
          <thead className='table-light'>
            <tr>
              <th>ชื่อ</th><th>ยอดกู้</th><th>ยอดรวม</th><th>จ่าย/งวด</th><th>โซน</th><th>วันที่</th><th>สถานะ</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const l = loans[c.key]
              const ins = installMap[c.key] || []
              return (
                <tr key={c.key}>
                  <td>{c.personal.fullName}</td>
                  <td>{l ? formatCurrency(l.actualAmount) : '-'}</td>
                  <td>{l ? formatCurrency(l.totalWithInterest) : '-'}</td>
                  <td>{ins.filter((i) => i.status === 'paid').length}/{ins.length}</td>
                  <td>{l?.zone || '-'}</td>
                  <td style={{ fontSize: 11 }}>{formatDate(c.meta.updatedAt)}</td>
                  <td>
                    <Badge bg={c.meta.status === 'stopped' ? 'danger' : 'secondary'} style={{ fontSize: 10 }}>
                      {c.meta.status === 'stopped' ? 'หนี้เสีย' : c.meta.status === 'closed' ? 'ปิดบัญชี' : 'ทำงานอยู่'}
                    </Badge>
                  </td>
                  <td>
                    <div className='d-flex gap-1'>
                      <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }}
                        onClick={() => { setDetailReg(c); setShowDetail(true) }}>
                        ดู
                      </Button>
                      <Button size='sm' variant='outline-warning' style={{ fontSize: 11 }}
                        onClick={() => { setDetailReg(c); setShowEdit(true) }}>
                        แก้ไข
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      <DetailModal
        show={showDetail}
        reg={detailReg}
        loan={detailReg ? (loans[detailReg.key] || null) : null}
        installments={detailReg ? (installMap[detailReg.key] || []) : []}
        onClose={() => { setShowDetail(false); setDetailReg(null) }}
      />
      <EditModal
        show={showEdit}
        reg={detailReg}
        loan={detailReg ? (loans[detailReg.key] || null) : null}
        tab={activeTab}
        uid={uid}
        onClose={() => { setShowEdit(false); setDetailReg(null) }}
        onReload={() => loadData(activeTab)}
      />
    </div>
  )
}